// The Help page's search rules, kept apart from the Angular page so they can be
// unit-tested: which help articles match the typed text, in what order, and the
// highlighted preview shown under each result. The page owns everything else
// (debounce, spinner, change detection, telemetry, navigation).
//
// Nothing here writes into the items it is given. The catalog the page holds
// lives for the whole visit, so a search hands back its previews per result
// instead of storing them on the shared entries.

import { calculateAsymTrigramMatching, generateTrigrams, splitTextIntoSentences } from "@cf-shared/shared-util";

/** how close (0..1) a trigram comparison must be before it counts as a match */
export const HELP_SEARCH_MATCH_THRESHOLD = 0.2;

/** how many sentences a result's preview keeps */
const PREVIEW_SENTENCE_COUNT = 2;

/** the parts of a catalog entry the search reads */
export interface HelpSearchItem {
  helpText: { title: string };
  trigrams: Set<string>; // trigrams of the title
  plainText: string; // the article body as plain text
}

export interface HelpSearchResult<T extends HelpSearchItem> {
  item: T;
  descriptionMarkdown: string; // the preview, with matching words in **bold**
}

/**
 * Run one search over the catalog. Title matches come first, each with a
 * preview even when no sentence matches. Then come articles whose body matches,
 * in catalog order, skipping any already listed as a title match.
 * `trimmedSearchText` must already be trimmed; an empty text finds nothing.
 */
export function searchHelpTexts<T extends HelpSearchItem>(items: T[], trimmedSearchText: string): HelpSearchResult<T>[] {
  if (trimmedSearchText.length === 0) {
    return [];
  }
  const searchTrigrams = generateTrigrams(trimmedSearchText);
  const lowercaseSearch = trimmedSearchText.toLocaleLowerCase();

  const titleMatches = items.filter(e =>
    calculateAsymTrigramMatching(searchTrigrams, e.trigrams) > HELP_SEARCH_MATCH_THRESHOLD ||
    e.helpText.title.toLocaleLowerCase().includes(lowercaseSearch));

  const results: HelpSearchResult<T>[] = titleMatches.map(item => ({
    item,
    descriptionMarkdown: buildHelpDescriptionMarkdown(item.plainText, trimmedSearchText, false),
  }));

  for (const item of items) {
    if (titleMatches.includes(item)) {
      continue;
    }
    const descriptionMarkdown = buildHelpDescriptionMarkdown(item.plainText, trimmedSearchText, true);
    if (descriptionMarkdown.length > 0) {
      results.push({ item, descriptionMarkdown });
    }
  }
  return results;
}

/**
 * Build the preview for one article: the first sentences that match the search,
 * with each matching word wrapped in `**`. When nothing matches, the article's
 * opening sentences are returned instead, or an empty string if
 * `onlyIfMatched` is set.
 */
export function buildHelpDescriptionMarkdown(plainText: string, trimmedSearchText: string, onlyIfMatched: boolean): string {
  const wordsToSearch = trimmedSearchText.split(/\s+/).filter(word => word.length > 0);
  const searchWordsTrigrams = wordsToSearch.map(word => generateTrigrams(word.trim()));
  const searchTextTrigrams = generateTrigrams(trimmedSearchText);

  const sentences = splitTextIntoSentences(plainText);
  let foundSentences: string[] = [];

  for (const sentence of sentences) {
    const wordsInSentence = sentence.split(/\s+/);
    let highlightedSentence = '';

    let matchingSentence = false;
    for (const word of wordsInSentence) {
      const normalizedWord = word.replace(/[.,?!]/g, '');

      let matchingWord = false;
      for (let iWordToSearch = 0; iWordToSearch < wordsToSearch.length; iWordToSearch++) {
        if (normalizedWord.toLowerCase().startsWith(wordsToSearch[iWordToSearch].toLowerCase()) ||
          (normalizedWord.includes(wordsToSearch[iWordToSearch]) && calculateAsymTrigramMatching(searchWordsTrigrams[iWordToSearch], generateTrigrams(normalizedWord.trim())) > HELP_SEARCH_MATCH_THRESHOLD)) {
          matchingSentence = true;
          matchingWord = true;
          highlightedSentence += `**${word}** `;
          break;
        }
      }
      if (!matchingWord) {
        highlightedSentence += `${word} `;
      }
    }

    if (matchingSentence) {
      foundSentences.push(highlightedSentence);
    } else {
      let sentenceTrigrams = generateTrigrams(sentence.trim());
      if ( // this is to avoid abbreviations like "e.g." or "i.e." to be matched
        sentence.length > 4 &&
        calculateAsymTrigramMatching(searchTextTrigrams, sentenceTrigrams) > HELP_SEARCH_MATCH_THRESHOLD
      ) {
        foundSentences.push(highlightedSentence);
      }
    }
  }

  if (foundSentences.length > 0) {
    return `${foundSentences.slice(0, PREVIEW_SENTENCE_COUNT).join('... ')}...`;
  } else if (!onlyIfMatched) {
    return `${sentences.slice(0, PREVIEW_SENTENCE_COUNT).join('. ')}...`;
  }
  return '';
}
